import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { useMe, goToLogin } from "@/hooks/use-me";
import { useSites } from "@/hooks/use-sites";
import { Button } from "@/components/ui/button";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import { SiteHeader } from "./site-header";
import type { Me } from "@/api";
import type { UseSitesResult } from "@/hooks/use-sites";

export interface AppOutletContext {
  me: Me;
  sites: UseSitesResult;
}

/** App shell adapted from prism-next's sidebar-01 + dashboard-01 blocks —
 *  replaces the old top tab-bar layout with a collapsible left Sidebar
 *  (nav + account) + a slim SiteHeader (sidebar toggle + title + site
 *  switcher) around a SidebarInset content area. */
export default function RootLayout() {
  const meState = useMe();
  const sites = useSites();
  const location = useLocation();

  // Surface-driven sidebar: expanded on the Home hero (`/`), collapsed to the
  // icon rail on the data surfaces. Re-applied on every navigation (keyed on
  // pathname) so each surface reasserts its default; a manual toggle in between
  // sticks until the next navigation.
  const [sidebarOpen, setSidebarOpen] = useState(location.pathname === "/");
  useEffect(() => {
    setSidebarOpen(location.pathname === "/");
  }, [location.pathname]);

  if (meState.status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-background" />;
  }

  if (meState.status === "signed-out") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-xs">
          <img src="/logo.avif" alt="Certu" width={160} height={57} />
          <h1 className="text-lg font-semibold">Shipment Explorer</h1>
          <p className="text-sm text-muted-foreground">Sign in to explore shipment routes and orders.</p>
          <Button onClick={goToLogin}>Sign in</Button>
        </div>
      </div>
    );
  }

  const { me } = meState;

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      className="h-svh overflow-hidden"
      style={{ "--sidebar-width": "calc(var(--spacing) * 64)" } as React.CSSProperties}
    >
      <AppSidebar me={me} variant="inset" />
      <SidebarInset>
        <SiteHeader sites={sites} />
        {sites.status === "no-access" ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="max-w-sm rounded-lg border bg-card p-6 text-center shadow-xs">
              <h2 className="font-semibold">No site access</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Your account hasn't been granted access to any site yet. Please contact an administrator.
              </p>
            </div>
          </div>
        ) : (
          <Outlet context={{ me, sites } satisfies AppOutletContext} />
        )}
      </SidebarInset>
    </SidebarProvider>
  );
}
